import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { JobDetailDto, JobSummaryDto } from "@workcost/shared";
import { useAuth } from "./AuthContext";
import { isMockApi, listActiveEstimates } from "./estimateApi";
import { getJob, listJobs } from "./jobsApi";
import { usageKey } from "./usage";

/** เรียก API งานได้เมื่อเข้าสู่ระบบแล้ว (หรือโหมด mock ใน dev) */
export function useApiReady(): { ready: boolean; anonymous: boolean; loading: boolean } {
  const { status } = useAuth();
  const mock = isMockApi();
  return { ready: mock || status === "authenticated", anonymous: !mock && status === "anonymous", loading: !mock && status === "loading" };
}

export const jobsKey = ["jobs"] as const;
export const jobKey = (id: string) => ["job", id] as const;

export function useJobs() {
  const { ready } = useApiReady();
  return useQuery({ queryKey: jobsKey, queryFn: listJobs, enabled: ready, staleTime: 30_000 });
}

export function useJob(id: string) {
  const { ready } = useApiReady();
  return useQuery({ queryKey: jobKey(id), queryFn: () => getJob(id), enabled: ready && id !== "", staleTime: 30_000 });
}

/** เขียนผลที่ server ตอบกลับลง cache ของรายละเอียดงาน และทำให้รายการงานโหลดใหม่ */
export function useApplyJob() {
  const qc = useQueryClient();
  return (job: JobDetailDto) => {
    qc.setQueryData(jobKey(job.jobId), job);
    qc.setQueryData<JobSummaryDto[] | undefined>(jobsKey, (old) =>
      old?.map((j) => (j.jobId === job.jobId ? { ...j, title: job.title, customerName: job.customerName, status: job.status, progress: job.progress, grandTotal: job.grandTotal, updatedAt: job.updatedAt } : j)),
    );
    void qc.invalidateQueries({ queryKey: jobsKey });
  };
}

export const activeEstimatesKey = ["estimates", "active"] as const;

/**
 * การประเมินที่กำลังทำอยู่ (ผู้ใช้ออกจากหน้ารอไปทำอย่างอื่น) ถามซ้ำทุก 3 วินาทีเมื่อยังมีรายการ
 * เมื่อรายการใดหายไป (ประเมินเสร็จหรือไม่สำเร็จ) ให้โหลดรายการงานและโควตาใหม่ เพื่อให้งานที่บันทึกอัตโนมัติโผล่ทันที
 */
export function useActiveEstimates() {
  const { ready } = useApiReady();
  const qc = useQueryClient();
  const prev = useRef<string[]>([]);
  const q = useQuery({
    queryKey: activeEstimatesKey,
    queryFn: listActiveEstimates,
    enabled: ready,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: (query) => ((query.state.data?.length ?? 0) > 0 ? 3000 : false),
  });
  useEffect(() => {
    if (!q.data) return;
    const now = q.data.map((x) => x.estimateId);
    if (prev.current.some((id) => !now.includes(id))) {
      void qc.invalidateQueries({ queryKey: jobsKey });
      void qc.invalidateQueries({ queryKey: usageKey });
    }
    prev.current = now;
  }, [q.data, qc]);
  return q;
}
